import { useQuery } from "@tanstack/react-query";
import { STORE_ROUTES, storeResponseSchema, type StoreWithItems } from "@nh/shared";
import { apiFetch } from "@/shared/api/client";

/** La vitrina pública de una tienda personalizada, por su enlace. */
export function usePublicStore(slug: string | undefined) {
  return useQuery<StoreWithItems>({
    queryKey: ["store-front", slug],
    queryFn: () =>
      apiFetch(STORE_ROUTES.public(slug as string), storeResponseSchema, { baseUrl: "" }),
    enabled: Boolean(slug),
  });
}

/**
 * La compra directa de la tienda personalizada, camino al checkout.
 *
 * ── Por qué NO usa el carrito de la casa ─────────────────────────────
 * El carrito global pinta precios del catálogo PÚBLICO; la tienda
 * personalizada vende al precio de SOCIO. Mezclar renglones de las dos
 * en un carrito sería enseñar dos reglas de precio en una lista — y el
 * servidor solo puede cobrar con una. La compra de tienda viaja aparte,
 * en `sessionStorage`, y el checkout la reconoce y cobra con
 * `storeSlug`.
 */
const STORE_CHECKOUT_KEY = "nh_store_checkout";

export interface StoreCheckoutDraft {
  storeSlug: string;
  storeName: string;
  /** `unitPriceCents` es el precio RESUELTO por el servidor en la
   * vitrina (propio del franquiciatario o de socio): el checkout lo
   * pinta tal cual, y el servidor lo recalcula al cobrar. */
  items: { productId: string; quantity: number; unitPriceCents: number }[];
}

export function writeStoreCheckout(draft: StoreCheckoutDraft): void {
  try {
    window.sessionStorage.setItem(STORE_CHECKOUT_KEY, JSON.stringify(draft));
  } catch {
    // Navegación privada: el checkout pedirá volver a elegir.
  }
}

export function readStoreCheckout(): StoreCheckoutDraft | null {
  try {
    const raw = window.sessionStorage.getItem(STORE_CHECKOUT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoreCheckoutDraft;
    return parsed?.storeSlug && Array.isArray(parsed.items) && parsed.items.length > 0
      ? parsed
      : null;
  } catch {
    return null;
  }
}

export function clearStoreCheckout(): void {
  try {
    window.sessionStorage.removeItem(STORE_CHECKOUT_KEY);
  } catch {
    // Nada que limpiar si no se pudo ni guardar.
  }
}
